import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFeatureFlags } from '../hooks/useFeatureFlags.jsx';
import { X, Bus, User, Calendar, Save, Wrench, Shield, Gauge, Fuel } from 'lucide-react';
import unifiedApi from '../services/unifiedApi';
import eventBus, { ASSIGNMENT_EVENTS } from '../utils/eventBus';
import logger from '@/utils/logger';

const BusFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  bus = null,
  mode = 'create', // 'create' or 'edit'
  loading = false,
}) => {
  const { isTabEnabled } = useFeatureFlags();
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    // Informations générales
    busId: '',
    imei: '',
    licensePlate: '',
    brand: 'Mercedes',
    model: '',
    year: new Date().getFullYear(),

    // Spécifications techniques
    capacity: 25,
    fuelType: 'diesel',
    mileage: 0,

    // Statut et assignation
    status: 'active',
    assignedDriver: '',
    assignedRoute: '',
    ignition_reliable: true,

    // Maintenance
    lastMaintenance: '',
    enginePowerCv: '',
    transmission: '',

    // Notes
    notes: '',
  });

  const [errors, setErrors] = useState({});

  // États pour les données réelles
  const [_drivers, setDrivers] = useState([]);
  const [_routes, setRoutes] = useState([]);
  const [_loadingData, setLoadingData] = useState(false);

  // Charger les données réelles pour l'assignation
  const loadAssignmentData = async () => {
    try {
      setLoadingData(true);
      const [driversResponse, routesResponse] = await Promise.all([
        unifiedApi.drivers.getAll(),
        unifiedApi.routes.getAll(),
      ]);

      // Afficher TOUS les chauffeurs actifs
      const allDrivers = driversResponse.data || [];
      const activeDrivers = allDrivers.filter((driver) => driver.status === 'active');

      setDrivers(activeDrivers);
      setRoutes(routesResponse.data || []);
    } catch (error) {
      logger.error('Erreur chargement données assignation:', error);
      setDrivers([]);
      setRoutes([]);
    } finally {
      setLoadingData(false);
    }
  };

  // Charger les données au montage du composant
  useEffect(() => {
    if (isOpen) {
      loadAssignmentData();
    }
  }, [isOpen]);

  // Écouter les événements d'assignation pour synchroniser les données
  useEffect(() => {
    if (!isOpen) return;

    const cleanupFunctions = [
      eventBus.on(ASSIGNMENT_EVENTS.DRIVER_UPDATED, () => loadAssignmentData()),
      eventBus.on(ASSIGNMENT_EVENTS.ROUTE_UPDATED, () => loadAssignmentData()),
      eventBus.on(ASSIGNMENT_EVENTS.ASSIGNMENT_CHANGED, (eventData) => {
        if (eventData.data.type !== 'bus') {
          loadAssignmentData();
        }
      }),
    ];

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [isOpen]);

  // Pré-remplir le formulaire en mode édition
  useEffect(() => {
    if (isOpen) {
      if (bus && mode === 'edit') {
        setFormData({
          busId: bus.busId || bus.bus_number || bus.id || '',
          imei: bus.imei || '',
          ignition_reliable: bus.ignition_reliable !== false, // default true if null/undefined
          licensePlate: bus.licensePlate || bus.license_plate || '',
          brand: bus.brand || 'Mercedes',
          model: bus.model || bus.full_model || '',
          year: bus.year || new Date().getFullYear(),
          capacity: bus.capacity || 25,
          fuelType: (() => {
            const v = bus.fuelType || bus.fuel_type || 'diesel';
            const map = {
              essence: 'gasoline',
              electrique: 'electric',
              hybride: 'hybrid',
            };
            return map[v] || v;
          })(),
          mileage: bus.mileage || bus.odometer_km || 0,
          status: bus.status || bus.operational_status || 'active',
          assignedDriver: bus.assignedDriver || '',
          assignedRoute: bus.assignedRoute || '',
          lastMaintenance: bus.lastMaintenance || bus.last_maintenance_date || '',
          enginePowerCv: bus.enginePowerCv ?? bus.engine_power_cv ?? '',
          transmission: bus.transmission || '',
          notes: bus.notes || '',
        });
      } else {
        // Reset form for create mode
        setFormData({
          busId: '',
          imei: '',
          ignition_reliable: true,
          licensePlate: '',
          brand: 'Mercedes',
          model: '',
          year: new Date().getFullYear(),
          capacity: 25,
          fuelType: 'diesel',
          mileage: 0,
          status: 'active',
          assignedDriver: '',
          assignedRoute: '',
          lastMaintenance: '',
          enginePowerCv: '',
          transmission: '',
          notes: '',
        });
      }
      setActiveTab('general');
      setErrors({});
    }
  }, [isOpen, bus, mode]);

  const handleInputChange = (field, value, nested = null) => {
    if (nested) {
      setFormData((prev) => ({
        ...prev,
        [nested]: {
          ...prev[nested],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validation des champs obligatoires - Onglet Général (toujours requis)
    if (!formData.licensePlate.trim()) newErrors.licensePlate = "La plaque d'immatriculation est obligatoire";
    if (!formData.model.trim()) newErrors.model = 'Le modèle est obligatoire';
    if (!formData.capacity || formData.capacity < 1) newErrors.capacity = 'La capacité doit être supérieure à 0';
    if (!formData.year || formData.year < 1990 || formData.year > new Date().getFullYear() + 1) {
      newErrors.year = "L'année doit être entre 1990 et " + (new Date().getFullYear() + 1);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Transformation des données pour le backend (camelCase -> snake_case)
      const submissionData = {
        bus_number: formData.busId,
        imei: formData.imei.trim() || null,
        license_plate: formData.licensePlate,
        // La base stocke « Marque Modèle » dans buses.model : trBuses.handleSave recompose les deux.
        brand: formData.brand,
        model: formData.model.trim(),
        year: formData.year,
        capacity: formData.capacity,
        fuel_type: formData.fuelType,
        status: formData.status,
        ignition_reliable: formData.ignition_reliable,
        odometer_km: formData.mileage || null,
        last_maintenance_date: formData.lastMaintenance || null,
        engine_power_cv: formData.enginePowerCv !== '' ? Number(formData.enginePowerCv) : null,
        transmission: formData.transmission || null,
        notes: formData.notes || null,
      };

      onSubmit(submissionData);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'general', label: 'Général', icon: Bus },
    {
      id: 'maintenance',
      label: 'Technique & Maintenance',
      icon: Wrench,
      featureGate: { module: 'buses', feature: 'maintenance' },
    },
  ];

  const activeTabs = tabs.filter(
    (tab) => !tab.featureGate || isTabEnabled(tab.featureGate.module, tab.featureGate.feature),
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Bus className="w-6 h-6 text-blue-600" />
                  </div>
                  {mode === 'create' ? 'Nouveau Bus' : 'Modifier le Bus'}
                </h2>
                <p className="text-gray-500 mt-1 ml-12">
                  {mode === 'create'
                    ? 'Ajoutez un nouveau véhicule à votre flotte'
                    : 'Modifiez les informations du véhicule'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="px-8 border-b border-gray-100 bg-gray-50/50">
              <div className="flex gap-6 overflow-x-auto no-scrollbar">
                {activeTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      relative py-4 px-2 text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap focus:outline-none focus-visible:outline-none
                      ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}
                    `}
                  >
                    <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'}`} />
                    {tab.label}
                    {activeTab === tab.id && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t-full"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8 bg-gray-50/30">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Onglet Général */}
                {activeTab === 'general' && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Immatriculation <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Bus className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={formData.licensePlate}
                            onChange={(e) => handleInputChange('licensePlate', e.target.value)}
                            className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                              errors.licensePlate
                                ? 'border-red-300 focus:border-red-500'
                                : 'border-gray-200 focus:border-blue-500'
                            }`}
                            placeholder="Ex: 12345-A-1"
                          />
                        </div>
                        {errors.licensePlate && <p className="text-xs text-red-500 mt-1">{errors.licensePlate}</p>}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Marque</label>
                        <select
                          value={formData.brand}
                          onChange={(e) => handleInputChange('brand', e.target.value)}
                          className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        >
                          <option value="Mercedes">Mercedes</option>
                          <option value="Volvo">Volvo</option>
                          <option value="Iveco">Iveco</option>
                          <option value="MAN">MAN</option>
                          <option value="Renault">Renault</option>
                          <option value="Scania">Scania</option>
                          <option value="Autre">Autre</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Modèle <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.model}
                          onChange={(e) => handleInputChange('model', e.target.value)}
                          className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                            errors.model
                              ? 'border-red-300 focus:border-red-500'
                              : 'border-gray-200 focus:border-blue-500'
                          }`}
                          placeholder="Ex: Sprinter"
                        />
                        {errors.model && <p className="text-xs text-red-500 mt-1">{errors.model}</p>}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Année</label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="number"
                            value={formData.year}
                            onChange={(e) => handleInputChange('year', e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Capacité (places) <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="number"
                            value={formData.capacity}
                            onChange={(e) => handleInputChange('capacity', e.target.value)}
                            className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                              errors.capacity
                                ? 'border-red-300 focus:border-red-500'
                                : 'border-gray-200 focus:border-blue-500'
                            }`}
                          />
                        </div>
                        {errors.capacity && <p className="text-xs text-red-500 mt-1">{errors.capacity}</p>}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Carburant</label>
                        <div className="relative">
                          <Fuel className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <select
                            value={formData.fuelType}
                            onChange={(e) => handleInputChange('fuelType', e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                          >
                            <option value="diesel">Diesel</option>
                            <option value="gasoline">Essence</option>
                            <option value="hybrid">Hybride</option>
                            <option value="electric">Électrique</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-gray-700">IMEI / ID Traceur GPS</label>
                        <input
                          type="text"
                          value={formData.imei}
                          onChange={(e) => handleInputChange('imei', e.target.value)}
                          placeholder="Ex: 352625090123456"
                          className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                          maxLength={25}
                        />
                        <p className="text-xs text-gray-500 mt-1">Identifiant unique du traceur GPS (IMEI).</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Statut</label>
                      <div className="relative">
                        <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <select
                          value={formData.status}
                          onChange={(e) => handleInputChange('status', e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer"
                        >
                          <option value="active">Actif</option>
                          <option value="maintenance">En maintenance</option>
                          <option value="inactive">Inactif</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Onglet Technique & Maintenance (fusionné) */}
                {activeTab === 'maintenance' && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Kilométrage</label>
                        <div className="relative">
                          <Gauge className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="number"
                            value={formData.mileage}
                            onChange={(e) => handleInputChange('mileage', e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Dernière maintenance</label>
                        <input
                          type="date"
                          value={formData.lastMaintenance}
                          onChange={(e) => handleInputChange('lastMaintenance', e.target.value)}
                          className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Puissance (CV)</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.enginePowerCv}
                          onChange={(e) => handleInputChange('enginePowerCv', e.target.value)}
                          placeholder="Ex: 170"
                          className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Transmission</label>
                        <select
                          value={formData.transmission}
                          onChange={(e) => handleInputChange('transmission', e.target.value)}
                          className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        >
                          <option value="">— Non renseigné —</option>
                          <option value="manual">Manuelle</option>
                          <option value="automatic">Automatique</option>
                        </select>
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-gray-700">Notes</label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => handleInputChange('notes', e.target.value)}
                          placeholder="Observations, historique, particularités du véhicule..."
                          rows={3}
                          maxLength={500}
                          className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </form>
            </div>

            {/* Footer */}
            <div className="px-8 py-6 border-t border-gray-100 bg-gray-50 flex justify-end items-center gap-3 sticky bottom-0 z-10">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-gray-700 font-medium hover:bg-gray-100 rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/20"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Traitement...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>{mode === 'create' ? 'Créer le bus' : 'Enregistrer les modifications'}</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BusFormModal;
